import type { Organizador } from '../../../services/organizadorService';
import type { Atleta } from '../../../services/ligaService';

/** Contexto em que o membro é organizador: liga ou torneio, com cargo */
export interface OrganizadorContexto {
  id: string;
  nome: string;
  imagemUrl?: string | null;
  cargo: string;
}

export interface TorneioInscrito {
  id: string;
  nome: string;
}

/** Membro unificado para as 4 abas da página de Membros */
export interface LeagueMember {
  /** Chave única para deduplicação por usuário (usuario.id) */
  key: string;
  /** ID do atleta para navegação para /userpage/:id; null = linha não clicável */
  atletaId: string | null;
  imagemUrl: string | null;
  nome: string;
  username: string | null;
  telefone: string | null;
  email: string | null;
  isColaborador: boolean;
  isSeguidor: boolean;
  isJogador: boolean;
  /** Liga/torneios em que é organizador e cargo (apenas se isColaborador) */
  organizadorContextos: OrganizadorContexto[];
  /** Torneios da liga em que o membro está inscrito */
  torneiosInscritos: TorneioInscrito[];
  /** Placeholder para futura exibição de torneios/liga que segue */
  seguidorPlaceholder?: boolean;
  /** Placeholder para futura exibição de torneios em que está inscrito */
  jogadorPlaceholder?: boolean;
}

function buildOrganizadorContexto(org: Organizador): OrganizadorContexto | null {
  const cargo = org.nomeFuncao?.trim() || 'Organizador';
  if (org.liga) {
    return {
      id: org.liga.id,
      nome: org.liga.nome,
      imagemUrl: org.liga.imagemUrl ?? undefined,
      cargo,
    };
  }
  if (org.torneio) {
    return {
      id: org.torneio.id,
      nome: org.torneio.nome,
      imagemUrl: org.torneio.avatarUrl ?? org.torneio.imagemUrl ?? undefined,
      cargo,
    };
  }
  return { id: org.id, nome: org.nome ?? 'Organizador', cargo };
}

/** Converte organizadores da liga (e por torneio) para membros unificados */
export function organizadoresToMembers(organizadores: Organizador[]): LeagueMember[] {
  const byUsuarioId = new Map<string, LeagueMember>();

  for (const org of organizadores) {
    const usuario = org.usuario;
    if (!usuario) continue;
    const userId = usuario.id;
    const atletaId = usuario.atleta?.id ?? null;
    const ctx = buildOrganizadorContexto(org);
    const existing = byUsuarioId.get(userId);
    if (existing) {
      if (ctx) existing.organizadorContextos.push(ctx);
      continue;
    }
    byUsuarioId.set(userId, {
      key: userId,
      atletaId,
      imagemUrl: usuario.imagemUrl ?? org.fotoUrl ?? null,
      nome: usuario.nome ?? org.nome ?? '—',
      username: usuario.username ?? null,
      telefone: org.telefone ?? null,
      email: usuario.email ?? null,
      isColaborador: true,
      isSeguidor: false,
      isJogador: false,
      organizadorContextos: ctx ? [ctx] : [],
      torneiosInscritos: [],
    });
  }

  return Array.from(byUsuarioId.values());
}

/** Converte atletas (seguidores ou jogadores) para membros unificados */
export function atletasToMembers(
  atletas: Atleta[],
  role: 'seguidor' | 'jogador'
): LeagueMember[] {
  return atletas.map((a) => ({
    key: a.usuario?.id ?? a.id,
    atletaId: a.id,
    imagemUrl: a.usuario?.imagemUrl ?? null,
    nome: a.usuario?.nome ?? '—',
    username: a.usuario?.username ?? null,
    telefone: a.telefone ?? null,
    email: a.usuario?.email ?? null,
    isColaborador: false,
    isSeguidor: role === 'seguidor',
    isJogador: role === 'jogador',
    organizadorContextos: [],
    torneiosInscritos: [],
    ...(role === 'seguidor' ? { seguidorPlaceholder: true } : {}),
    ...(role === 'jogador' ? { jogadorPlaceholder: true } : {}),
  }));
}

/** Mescla colaboradores, seguidores e jogadores na aba "Todos", deduplicando por key (usuario.id) e agregando papéis */
export function mergeMembersForAllTab(
  colaboradores: LeagueMember[],
  seguidores: LeagueMember[],
  jogadores: LeagueMember[]
): LeagueMember[] {
  const map = new Map<string, LeagueMember>();

  function add(m: LeagueMember) {
    const key = m.key;
    const existing = map.get(key);
    if (!existing) {
      map.set(key, { ...m });
      return;
    }
    existing.isColaborador = existing.isColaborador || m.isColaborador;
    existing.isSeguidor = existing.isSeguidor || m.isSeguidor;
    existing.isJogador = existing.isJogador || m.isJogador;
    if (m.atletaId) existing.atletaId = m.atletaId;
    if (m.organizadorContextos.length) {
      existing.organizadorContextos = [
        ...existing.organizadorContextos,
        ...m.organizadorContextos,
      ];
    }
    if (m.telefone && !existing.telefone) existing.telefone = m.telefone;
    if (m.email && !existing.email) existing.email = m.email;
    if (m.torneiosInscritos.length) {
      const existingIds = new Set(existing.torneiosInscritos.map(t => t.id));
      const novos = m.torneiosInscritos.filter(t => !existingIds.has(t.id));
      if (novos.length) {
        existing.torneiosInscritos = [...existing.torneiosInscritos, ...novos];
      }
    }
    if (m.seguidorPlaceholder) existing.seguidorPlaceholder = true;
    if (m.jogadorPlaceholder) existing.jogadorPlaceholder = true;
  }

  colaboradores.forEach(add);
  seguidores.forEach(add);
  jogadores.forEach(add);

  const result = Array.from(map.values());
  result.sort((a, b) => (a.nome || '').localeCompare(b.nome || '', 'pt-BR'));
  return result;
}

/** Enriquece membros jogadores com seus torneios inscritos na liga. */
export function enrichMembersWithTorneios(
  members: LeagueMember[],
  torneiosPorAtleta: Map<string, TorneioInscrito[]>
): LeagueMember[] {
  return members.map((m) => {
    if (!m.atletaId || !m.isJogador) return m;
    const torneios = torneiosPorAtleta.get(m.atletaId);
    if (!torneios?.length) return m;
    return { ...m, torneiosInscritos: torneios };
  });
}
